import {useCallback, useEffect, useState} from 'react';

import {getAccountProductFilterOptions} from 'src/openapi/admin/apis/GetAccountProductFilterOptionsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {ProductSummary} from 'src/openapi/admin/models/ProductSummary';

type Result = {
    accountId: number, data?: ProductSummary[], error?: ApiError, loading: boolean, names: Record<string, string>,
};

export const useAccountProductOptions = (accountId: number, refreshVersion: number, enabled: boolean): Omit<Result, 'accountId'> & {retry: () => void} => {
    const dispatch = useAppDispatch();
    const [retryVersion, setRetryVersion] = useState(0);
    const [result, setResult] = useState<Result>({accountId, loading: true, names: {}});
    const retry = useCallback(() => setRetryVersion(version => version + 1), []);

    useEffect(() => {
        if (!enabled) {
            setResult({accountId, loading: false, names: {}});
            return;
        }
        const controller = new AbortController();
        setResult(previous => ({
            accountId, loading: true, data: previous.accountId === accountId ? previous.data : undefined,
            names: previous.accountId === accountId ? previous.names : {},
        }));
        const load = async () => {
            try {
                const response = await dispatch(getAccountProductFilterOptions(
                    {accountId}, {signal: controller.signal},
                ));
                if (controller.signal.aborted) {
                    return;
                }
                if (response.payload.success) {
                    const data = response.payload.data;
                    setResult(previous => ({accountId, data, loading: false,
                        names: {...previous.names, ...Object.fromEntries(data.map(product => (
                            [product.productExternalKey, product.name]
                        )))},
                    }));
                } else {
                    const error = response.payload.data;
                    setResult(previous => ({...previous, error, loading: false}));
                }
            } catch {
                if (!controller.signal.aborted) {
                    setResult(previous => ({
                        ...previous, error: {message: 'Failed to load products'}, loading: false,
                    }));
                }
            }
        };
        load();
        return () => controller.abort();
    }, [accountId, refreshVersion, retryVersion, dispatch, enabled]);

    return {...enabled && result.accountId === accountId
        ? result : {loading: enabled, data: undefined, error: undefined, names: {}}, retry};
};
